'use client';

import { useLocalToken } from '@/hooks/use-local-token';
import { cn } from '@/lib/utils/css';
import { apiPaths } from '@/routes/paths';
import { usePlausible } from 'next-plausible';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

type Props = {
  couponId: string;
  healthy?: boolean;
  merchantDomain: string;
  children: React.ReactNode;
  className?: string;
};

export default function CouponClickWrapper({
  merchantDomain,
  couponId,
  healthy,
  children,
  className,
  ...rest
}: Props) {
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const affLinkOpen = useRef(false);
  const router = useRouter();
  const localUserToken = useLocalToken();
  const plausible = usePlausible();

  useEffect(() => {
    const urlSlug = merchantDomain;
    const id = searchParams.get('id');

    if (couponId === id) {
      plausible('coupon-viewed', { props: { urlSlug, couponId, healthy } });
    }
  }, [merchantDomain, couponId, searchParams, plausible, healthy]);

  useEffect(() => {
    if (searchParams.get('id')) {
      affLinkOpen.current = true;
    }
  }, [searchParams]);

  const handleClick = () => {
    if (!localUserToken || !couponId) return;

    const searchParams = new URLSearchParams({ id: couponId });
    const localURL = pathName + '?' + searchParams.toString();

    if (affLinkOpen.current) {
      router.replace(localURL, { scroll: false });
      return;
    }

    // Open merchant domain in the current tab
    window.open(localURL, '_blank');
    // Open coupon details in another new tab
    window.location.href = apiPaths.redirect(merchantDomain);
  };

  return (
    <div
      onClick={handleClick}
      className={cn('cursor-pointer', className)}
      {...rest}
    >
      {children}
    </div>
  );
}
